import { useState } from 'react'
import { Link } from 'react-router-dom'
import { requestPasswordReset } from '../api/auth.js'
import { BrandLockup } from '../components/Brand.jsx'

/**
 * Public "I forgot my password" form.
 *
 * The backend always returns 200 regardless of whether the email is
 * known — this UI mirrors that posture: same generic confirmation
 * shown for any submission. Don't leak which addresses exist.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [err, setErr] = useState(null)

  async function onSubmit(e) {
    e.preventDefault()
    setErr(null)
    if (!email.trim()) { setErr('Please enter your email.'); return }
    setBusy(true)
    try {
      await requestPasswordReset(email.trim())
      setSubmitted(true)
    } catch (e) {
      // Even if the network errors, show the generic success state —
      // the backend already swallows email-existence checks.
      setSubmitted(true)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-wantok-50 via-cream to-coral-50/50 flex flex-col">
      <header className="bg-wantok-gradient text-white">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <BrandLockup variant="light" linkTo="/login" size={48} />
          <Link to="/login" className="text-xs uppercase tracking-[0.18em] font-bold text-white/80 hover:text-white">
            ← Back to sign in
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-card p-6">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-wantok-700">Account recovery</div>
          <h1 className="text-2xl font-display font-bold text-slate-900 mt-1">Reset your password</h1>

          {submitted ? (
            <div className="mt-4 space-y-3">
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800">
                <div className="font-semibold">Check your email</div>
                <p className="mt-1 text-emerald-700">
                  If an account exists for <span className="font-mono">{email}</span>, we've sent a reset link.
                  The link is valid for a limited time.
                </p>
              </div>
              <p className="text-xs text-slate-500">
                Didn't receive it? Check your spam folder, or try again with a different email address.
              </p>
              <Link to="/login" className="block text-sm text-center text-wantok-700 underline font-medium">
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-4 space-y-4">
              <p className="text-sm text-slate-600">
                Enter the email you used to sign up. We'll send you a link to choose a new password.
              </p>
              <label className="block text-xs text-slate-700 font-semibold">
                Email
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  autoFocus required autoComplete="email"
                  className="mt-1 w-full border rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-wantok-400 font-normal"
                />
              </label>
              {err && (
                <div className="text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2 text-sm">
                  {err}
                </div>
              )}
              <button type="submit" disabled={busy}
                      className="w-full bg-wantok-700 hover:bg-wantok-800 text-white rounded-full px-5 py-2 text-sm font-semibold disabled:opacity-60">
                {busy ? 'Sending link…' : 'Send reset link'}
              </button>
              <div className="text-center text-xs text-slate-500 pt-2 border-t border-slate-100">
                Remembered it?{' '}
                <Link to="/login" className="text-wantok-700 underline font-medium">Sign in</Link>
              </div>
            </form>
          )}
        </div>
      </main>
    </div>
  )
}
